import React, { useState, useCallback, useEffect } from 'react';
import { View, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import { CRMParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import MyDataTable from '../../../components/MyDataTable';
import CustomButton from '../../../components/CustomButton';
import DropdownButton from '../../../components/DropdownButton';
import moment from 'moment';
import { useAuth } from '../../../context/Auth';
import { useQuery } from '@tanstack/react-query';
import { crmService } from '../../../services/central-api/crm';
import Filter from './Filters';
import TicketReasonModal from './TicketReasonModal';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import Header from '../../../components/Header';
import Container from '../../../components/Container';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useCurrentApp } from '../../../context/CurrentApp';

export default function TicketReasons({ navigation }: { navigation: Navigation; }) {
    const { isParentApp } = useCurrentApp();

    const route = useRoute<RouteProp<CRMParamList, 'TicketReasons'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<CRMParamList['TicketReasons']>({
        ...route.params,
        page: Math.max(+route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [totalItems, setTotalItems] = useState(0);

    const [selectedTicketReason, setSelectedTicketReason] = useState<PaginatedTicketReason>();
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [ticketReasons, setTicketReasons] = useState<PaginatedTicketReason[]>([]);
    const [ticketReasonModal, setTicketReasonModal] = useState<boolean>(false);

    const queryResult = useQuery({
        queryKey: ['ticketReasons', filters],
        queryFn: () => crmService.getPaginatedTicketReason(filters),
        enabled: !!authenticatedUser,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setTicketReasons(data.rows);
        setTotalItems(data.count);
    }, [data]);

    async function confirmDelete(ticketReason: PaginatedTicketReason) {
        try {
            await crmService.deleteTicketReason(ticketReason.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            setDeleteModalVisible(false);
            setSelectedTicketReason(undefined);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            setTotalItems(0);
            refetch();
        }
    }, [isFocused, filters]));

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <Container>
            <Header
                title={translate('drpScenarios')}
                actions={isParentApp && <CustomButton
                    text={translate('register')}
                    onPress={() => {
                        setSelectedTicketReason(undefined);
                        setTicketReasonModal(true);
                    }}
                />}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredScenarios')}
                    zIndex={2}
                    filter={<Filter
                        filters={filters}
                        setFilters={setFilters}
                        textFilter={textFilter}
                        setTextFilter={setTextFilter}
                    />}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />

                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('company'),
                                selector: row => row.company?.name || '',
                                grow: 2,
                                wrap: true,
                                omit: !isParentApp
                            },
                            {
                                name: 'ID',
                                selector: r => r.id,
                                grow: 0
                            },
                            {
                                name: translate('color'),
                                grow: 0,
                                cell: row => <View style={{ backgroundColor: row.color, height: 20, width: 20, borderRadius: 4 }}></View>
                            },
                            {
                                name: translate('name'),
                                selector: row => row.name,
                                wrap: true,
                            },
                            {
                                name: translate('defaultReason'),
                                selector: row => translate(row.isDefault ? 'yes' : 'no'),
                                wrap: true,
                                omit: !isParentApp
                            },
                            {
                                name: translate('requiresAttachment'),
                                selector: row => translate(row.requiresAttachment ? 'yes' : 'no'),
                                wrap: true,
                                omit: !isParentApp
                            },
                            {
                                name: translate('durationInMinutes'),
                                selector: row => row.duration,
                                wrap: true,
                            },
                            {
                                name: translate('createdAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
                                wrap: true,
                            },
                            {
                                name: '',
                                cell: row => {
                                    return (
                                        <DropdownButton actions={[{
                                            content: translate('editInformation'),
                                            onPress: () => {
                                                setSelectedTicketReason(row);
                                                setTicketReasonModal(true);
                                            }
                                        }, {
                                            content: translate('delete'),
                                            variant: 'danger',
                                            onPress: () => {
                                                setSelectedTicketReason(row);
                                                setDeleteModalVisible(true);
                                            }
                                        }]} />
                                    );
                                },
                                wrap: true,
                                button: true,
                                omit: !isParentApp
                            }
                        ]}
                        data={ticketReasons}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={totalItems}
                        setPage={page => {
                            setFilters(old => ({ ...old, page }));
                            navigation.setParams({ ...route.params, page: page + 1 });
                        }}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                            navigation.setParams({ ...route.params, limit: pageSize, page: 1 });
                        }}
                    />}
                />
            </Card>

            {selectedTicketReason &&
                <DeleteModal
                    setModalVisible={setDeleteModalVisible}
                    isModalVisible={deleteModalVisible}
                    itemName={selectedTicketReason.name}
                    onSubmit={async () => {
                        await confirmDelete(selectedTicketReason);
                    }}
                    onCancel={() => {
                        setSelectedTicketReason(undefined);
                        refetch();
                    }}
                />
            }

            {isParentApp && <TicketReasonModal
                selectedTicketReasonId={selectedTicketReason?.id}
                isModalVisible={ticketReasonModal}
                setModalVisible={setTicketReasonModal}
                onClose={() => {
                    setSelectedTicketReason(undefined);
                    refetch();
                }}
            />}
        </Container>
    );
}
